import { FC, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../Modal'
import PerText from '../Text'
import PerFlexRow from '../FlexRow'
import { KudosFilterInput, KudosType, EmployeeSearch, CompanyScope, CompanyTiny } from '../../../types'
import { KudosIconIndicator } from '../../../modules/kudos/components/KudosIconIndicator'
import TextArea from 'antd/lib/input/TextArea'
import { createKudos } from '../../../graphql/kudos/useKudosMutations'
import { EmployeeSearchSelect } from '../../../modules/kudos/components/EmployeeSearchSelect'
import { computeFullname } from '../../../shared/nameDisplayPreferencesUtil'
import { useAuth } from '../../../context/auth-context'
import { getKudosColor } from '../../../shared/kudosUtil'
import { useActiveEmployeeSearch } from '../../../modules/employees/EmployeeSearch/useActiveEmployeeSearch'
import { showNotification } from '../Notification/notification'
import { useCompanySearch } from '../../../graphql/company/hooks/useCompanySearch'
import { CompanySearchSelect } from '../../../modules/kudos/components/CompanySearchSelect'
import CreateKudosModalGuidelines from './CreateKudosModalGuidelines'

export interface CreateKudosModalWithEmployeeSelectProps {
  visible: boolean
  closeModal: () => void
  onSuccess?: () => void
  filters: KudosFilterInput
  replyerView: boolean
}

export const CreateKudosModalWithEmployeeSelect: FC<CreateKudosModalWithEmployeeSelectProps> = ({
  visible,
  closeModal,
  onSuccess,
  filters,
  replyerView,
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()

  const [description, setDescription] = useState<string>('')
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeSearch | null>(null)
  const [selectedEmployeeCompany, setSelectedEmployeeCompany] = useState<CompanyTiny | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [guidelinesAccepted, setGuidelinesAccepted] = useState<boolean>(false)

  const employeeSearch = useActiveEmployeeSearch(true, selectedEmployeeCompany?.id || undefined)
  const companySearch = useCompanySearch(CompanyScope.All)

  const handleCreate = async () => {
    if (!description.trim() || !selectedEmployee) return

    setIsLoading(true)
    setErrorMessage('')

    const result = await createKudos(selectedEmployee.id, description.trim(), filters, replyerView)

    if (!!result.data && !result.errors) {
      resetFormAndCloseModal()
      onSuccess?.()
      showNotification({
        type: 'success',
        message: t('kudos.notifications.create.successTitle'),
        description: t('kudos.notifications.create.successDescription'),
      })
    } else {
      const errorMessage = result.errors?.[0]?.extensions?.validationErrors?.[0]?.message || t('kudos.createModal.errors.generic')
      setErrorMessage(errorMessage)
      setIsLoading(false)
    }
  }

  const resetFormAndCloseModal = () => {
    setDescription('')
    setGuidelinesAccepted(false)
    setSelectedEmployee(null)
    setSelectedEmployeeCompany(null)
    setErrorMessage('')
    setIsLoading(false)
    employeeSearch.handleClear()
    companySearch.handleClear()
    closeModal()
  }

  const handleEmployeeOptionClick = (employee: EmployeeSearch) => {
    setSelectedEmployee(employee)
    employeeSearch.handleClear()
  }

  const handleEmployeeClear = () => {
    setSelectedEmployee(null)
    employeeSearch.handleClear()
  }

  const handleEmployeeCompanyOptionClick = useCallback(
    (company: CompanyTiny) => {
      setSelectedEmployeeCompany(company)
      companySearch.handleClear()
    },
    [companySearch],
  )

  const handleCompanyClear = useCallback(() => {
    setSelectedEmployeeCompany(null)
    companySearch.handleClear()
  }, [companySearch])

  const isFormValid = description.trim() && selectedEmployee

  return (
    <PerModal
      title={{ heading: t('kudos.createModal.title') }}
      size="large"
      centered
      visible={visible}
      onCancel={resetFormAndCloseModal}
      footer={{
        cancelBtn: {
          text: t('global.button.cancel'),
          onClick: resetFormAndCloseModal,
          icon: 'close_stroke',
          visible: !isLoading,
        },
        confirmBtn: {
          altText: t('kudos.createModal.title'),
          onClick: handleCreate,
          disabled: !isFormValid || isLoading || !guidelinesAccepted,
          isProcessing: { active: isLoading },
          tooltip: !isFormValid
            ? t('kudos.createModal.disabledButtonTooltip')
            : !guidelinesAccepted
            ? t('kudos.createModal.guidelines.tooltip')
            : undefined,
        },
      }}
    >
      <PerFlexRow direction="column">
        {/* Disclaimer */}
        <PerText fontSize="m" mb="m">
          {t('kudos.createModal.disclaimer')}
        </PerText>

        {/* Kudos Info */}
        <PerFlexRow align="middle" mb="m">
          <KudosIconIndicator kudosType={KudosType.Positive} />
          <PerFlexRow align="middle">
            <PerText fontSize="l" ml="xs">
              {t('kudos.createModal.forEmployee')}
            </PerText>
            <PerText fontSize="l" fontWeight="bold" ml="xs" color={getKudosColor(KudosType.Positive)}>
              {selectedEmployee && computeFullname(user.nameDisplayPreference, selectedEmployee.firstName, selectedEmployee.lastName)}
            </PerText>
          </PerFlexRow>
        </PerFlexRow>

        {/* Company Selection */}
        <PerFlexRow direction="column" mb="m">
          <PerText fontSize="m" fontWeight="bold" mb="xxs">
            {t('kudos.createModal.company')}
          </PerText>
          <CompanySearchSelect
            searchTerm={companySearch.searchTerm}
            onSearch={companySearch.handleSearch}
            onClear={handleCompanyClear}
            companies={companySearch.companies}
            loading={companySearch.loading}
            shouldShowOptions={companySearch.shouldShowOptions}
            onOptionClick={handleEmployeeCompanyOptionClick}
            placeholder={t('kudos.management.filters.employeeCompanyPlaceholder')}
            selectedCompany={selectedEmployeeCompany}
            size={'default'}
            disabled={!!selectedEmployee}
            optionMaxWidth={550}
          />
          {selectedEmployee && (
            <PerText fontSize="s" mt="xxs" color="secondary">
              {t('kudos.createModal.companyDisabled')}
            </PerText>
          )}
        </PerFlexRow>

        {/* Employee Selection */}
        <PerFlexRow direction="column" mb="m">
          <PerText fontSize="m" fontWeight="bold" mb="xxs">
            {t('kudos.createModal.employee')}
          </PerText>
          <EmployeeSearchSelect
            searchTerm={employeeSearch.searchTerm}
            onSearch={employeeSearch.handleSearch}
            onClear={handleEmployeeClear}
            employees={employeeSearch.employees}
            loading={employeeSearch.loading}
            shouldShowOptions={employeeSearch.shouldShowOptions}
            onOptionClick={handleEmployeeOptionClick}
            placeholder={t(`kudos.createModal.${selectedEmployeeCompany ? 'employeeWithCompanyPlaceholder' : 'employeePlaceholder'}`, {
              company: selectedEmployeeCompany?.name,
            })}
            selectedEmployee={selectedEmployee}
            size={'default'}
            optionMaxWidth={550}
          />
          {!selectedEmployeeCompany && !selectedEmployee && (
            <PerText fontSize="s" mt="xxs" color="secondary">
              {t('kudos.createModal.employeeSearchInfo')}
            </PerText>
          )}
        </PerFlexRow>

        {errorMessage && (
          <PerText color="error" fontSize="m" mb="m">
            {errorMessage}
          </PerText>
        )}

        {/* Description */}
        <PerFlexRow direction="column">
          <PerText fontSize="m" fontWeight="bold" mb="xxs">
            {t('kudos.createModal.description')}
          </PerText>
          <TextArea
            rows={4}
            placeholder={t('kudos.createModal.descriptionPlaceholder')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={1000}
          />
        </PerFlexRow>
        <CreateKudosModalGuidelines guidelinesAccepted={guidelinesAccepted} setGuidelinesAccepted={setGuidelinesAccepted} />
      </PerFlexRow>
    </PerModal>
  )
}

export default CreateKudosModalWithEmployeeSelect
